/**
 * KAN-1641 — zákazník a zrušení objednávky.
 *
 * Rozhodnutí vlastníka 2026-09-25: dokud objednávka není zaplacená, zákazník
 * ji zruší sám; potom jen „Požádat o zrušení" a rozhodne obchod. Která z těch
 * dvou věcí jde, říká server (``available_actions``: ``cancel`` /
 * ``request_cancel``) — tady se stav objednávky nečte.
 *
 * KAN-1860 — stav se čte jen pro TEXT žádosti:
 * ``cancel.requestDescriptionByStatus.<stav>`` (odeslaná = zboží je na cestě);
 * stav bez vlastního textu dostane obecný ``cancel.requestDescription``.
 */

import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCustomerCancelOrder, useCustomerCancelReasons } from "@/api/queries/orders";
import { ApiError } from "@/lib/api";

import { CancelOrderDialog } from "./CancelOrderDialog";

export function CustomerCancelOrder({
  orderNumber,
  actions,
  lang,
  status,
}: {
  orderNumber: string;
  actions: readonly string[];
  lang: string;
  status?: string;
}): JSX.Element | null {
  const { t } = useTranslation("order");
  const kind = actions.includes("cancel")
    ? "cancel"
    : actions.includes("request_cancel")
      ? "request"
      : null;
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const reasons = useCustomerCancelReasons(lang, { enabled: open });
  const mutation = useCustomerCancelOrder({
    onSuccess: (_data, vars) => {
      setOpen(false);
      if (vars.kind === "request") setRequested(true);
    },
  });

  if (requested) {
    return (
      <p className="text-sm text-ink-2" role="status" data-testid="order-cancel-requested">
        {t("cancel.requestSent")}
      </p>
    );
  }
  if (kind === null) return null;

  const errorCode = mutation.error instanceof ApiError ? mutation.error.code : null;
  const isRequest = kind === "request";

  return (
    <div className="border-t border-border pt-3">
      <button
        type="button"
        className="text-sm text-ink-2 underline hover:text-ink"
        onClick={() => {
          mutation.reset();
          setOpen(true);
        }}
        data-testid={isRequest ? "order-request-cancel" : "order-customer-cancel"}
      >
        {isRequest ? t("cancel.request") : t("cancel.action")}
      </button>
      {open && (
        <CancelOrderDialog
          title={isRequest ? t("cancel.requestTitle") : t("cancel.title")}
          description={
            isRequest
              ? t(`cancel.requestDescriptionByStatus.${status ?? ""}`, {
                  defaultValue: t("cancel.requestDescription"),
                })
              : t("cancel.description")
          }
          reasonLabel={t("cancel.reason")}
          notePlaceholder={t("cancel.notePlaceholder")}
          confirmLabel={isRequest ? t("cancel.confirmRequest") : t("cancel.confirm")}
          cancelLabel={t("cancel.keep")}
          reasonRequired={t("cancel.reasonRequired")}
          reasons={reasons.data ?? []}
          busy={mutation.isPending}
          error={
            mutation.isError
              ? t(`cancel.errors.${errorCode ?? "generic"}`, {
                  defaultValue: t("cancel.errors.generic"),
                })
              : null
          }
          onConfirm={(reason, note) => mutation.mutate({ orderNumber, reason, note, kind })}
          onClose={() => setOpen(false)}
          testId="order-customer-cancel-dialog"
        />
      )}
    </div>
  );
}
